import {definePreset, palette} from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import {PrimeNGConfigType} from 'primeng/config';
import {NOMBRE_PLANTILLA, NombrePlantilla} from './plantilla';

// Color primario de cada plantilla: el --primary-color de su antiguo tema CSS (Mirage / Avalon)
const COLOR_PRIMARIO: Record<NombrePlantilla, string> = {
    teki: '#5E81AC',
    cbet: '#0d6efd'
};

// Grises de los antiguos temas CSS (iguales en ambas plantillas), en la escala de Lara: 0 el más claro, 950 el más oscuro.
// En oscuro Lara usa 950 para campos, 900 para contenido, 800 para hover y 700 para bordes.
const SUPERFICIE_CLARA = {
    0: '#ffffff', 50: '#f9fafb', 100: '#f8f9fa', 200: '#e9ecef', 300: '#dee2e6', 400: '#ced4da',
    500: '#adb5bd', 600: '#6c757d', 700: '#495057', 800: '#343a40', 900: '#212529', 950: '#121416'
};
const SUPERFICIE_OSCURA = {
    0: '#ffffff', 50: '#f4f4f5', 100: '#e9e9ea', 200: '#d2d4d5', 300: '#bcbec0', 400: '#a6a8ab',
    500: '#797d82', 600: '#56606d', 700: '#3f4b5b', 800: '#343e4b', 900: '#2a323d', 950: '#20262e'
};

// Como en Mirage / Avalon, lo seleccionado (opción, página, botón) se marca con el primario sólido
const RESALTADO = {
    background: '{primary.color}',
    focusBackground: '{primary.hover.color}',
    color: '{primary.contrast.color}',
    focusColor: '{primary.contrast.color}'
};

const BOTON_PRIMARIO = {
    background: '{primary.color}', hoverBackground: '{primary.hover.color}', activeBackground: '{primary.active.color}',
    color: '{primary.contrast.color}', hoverColor: '{primary.contrast.color}', activeColor: '{primary.contrast.color}'
};

// Severidades de botón de los antiguos temas (iguales en ambas plantillas): [fondo, hover, activo], texto, [borde, hover, activo]
const severidad = (fondos: string[], color: string, bordes: string[]) => ({
    background: fondos[0], hoverBackground: fondos[1], activeBackground: fondos[2],
    borderColor: bordes[0], hoverBorderColor: bordes[1], activeBorderColor: bordes[2],
    color, hoverColor: color, activeColor: color
});
const SECUNDARIO = severidad(['#6c757d', '#5a6268', '#545b62'], '#ffffff', ['#6c757d', '#5a6268', '#4e555b']);
const BOTONES_CLAROS = {
    secondary: SECUNDARIO,
    info: severidad(['#17a2b8', '#138496', '#138496'], '#ffffff', ['#17a2b8', '#117a8b', '#117a8b']),
    success: severidad(['#28a745', '#218838', '#1e7e34'], '#ffffff', ['#28a745', '#1e7e34', '#1c7430']),
    warn: severidad(['#ffc107', '#e0a800', '#d39e00'], '#212529', ['#ffc107', '#d39e00', '#c69500']),
    help: severidad(['#6f42c1', '#633bad', '#58349a'], '#ffffff', ['#6f42c1', '#58349a', '#4d2e87']),
    danger: severidad(['#dc3545', '#c82333', '#bd2130'], '#ffffff', ['#dc3545', '#bd2130', '#b21f2d'])
};
const BOTONES_OSCUROS = {
    secondary: SECUNDARIO,
    info: severidad(['#7fd8e6', '#4cc8db', '#26bdd3'], '#151515', ['#4cc8db', '#26bdd3', '#00b2cc']),
    success: severidad(['#9fdaa8', '#78cc86', '#5ac06c'], '#151515', ['#78cc86', '#5ac06c', '#3cb553']),
    warn: severidad(['#ffe082', '#ffd54f', '#ffca28'], '#151515', ['#ffd54f', '#ffca28', '#ffc107']),
    help: severidad(['#b7a2e0', '#9a7cd4', '#845fca'], '#151515', ['#9a7cd4', '#845fca', '#6d43c0']),
    danger: severidad(['#f19ea6', '#e97984', '#f75965'], '#151515', ['#e97984', '#f75965', '#fd464e'])
};

// En oscuro Mirage mantiene el mismo primario con texto blanco; Avalon lo aclara con texto oscuro (lo que ya hace Lara)
const PRIMARIO_OSCURO: Record<NombrePlantilla, object> = {
    teki: {color: '{primary.500}', contrastColor: '#ffffff', hoverColor: '{primary.600}', activeColor: '{primary.700}'},
    cbet: {}
};

// Densidad de los antiguos temas: campos y botones con padding 0.5rem 0.75rem y radio de 4px
const PresetPlantilla = definePreset(Lara, {
    primitive: {
        borderRadius: {md: '4px'}
    },
    semantic: {
        primary: palette(COLOR_PRIMARIO[NOMBRE_PLANTILLA]),
        formField: {paddingX: '0.75rem', paddingY: '0.5rem'},
        colorScheme: {
            // Un campo deshabilitado conserva su fondo y solo atenúa el texto, como en los antiguos temas
            light: {surface: SUPERFICIE_CLARA, highlight: RESALTADO, formField: {disabledBackground: '{surface.0}'}},
            dark: {
                surface: SUPERFICIE_OSCURA, primary: PRIMARIO_OSCURO[NOMBRE_PLANTILLA], highlight: RESALTADO,
                formField: {disabledBackground: '{surface.950}'}
            }
        }
    },
    components: {
        button: {
            root: {paddingX: '0.75rem', iconOnlyWidth: '2.357rem'},
            colorScheme: {
                light: {root: BOTONES_CLAROS},
                dark: {root: BOTONES_OSCUROS}
            }
        },
        togglebutton: {
            root: {checkedBorderColor: '{primary.color}'}
        },
        paginator: {
            navButton: {borderRadius: '{content.border.radius}'}
        },
        // El botón del calendario era un p-button primario
        datepicker: {
            dropdown: {
                borderColor: '{primary.color}', hoverBorderColor: '{primary.hover.color}', activeBorderColor: '{primary.active.color}'
            },
            colorScheme: {
                light: {dropdown: BOTON_PRIMARIO},
                dark: {dropdown: BOTON_PRIMARIO}
            }
        }
    }
});

/**
 * Tema de componentes PrimeNG. El modo oscuro sigue la clase .tema-oscuro que plantilla.ts pone en <html>,
 * y los estilos de PrimeNG van en una capa CSS para que los overrides propios (.p-*) sigan teniendo prioridad.
 */
export const CONFIG_PRIMENG: PrimeNGConfigType = {
    ripple: true,
    // Textos propios de los componentes (calendario, mensajes vacíos, botones por defecto). Se mezclan con los de
    // PrimeNG, así que dateFormat y firstDayOfWeek siguen como estaban.
    translation: {
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre',
            'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        weekHeader: 'Sm',
        accept: 'Sí',
        reject: 'No',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
        emptyMessage: 'No se encontraron resultados',
        emptyFilterMessage: 'No se encontraron resultados',
        emptySearchMessage: 'No se encontraron resultados'
    },
    theme: {
        preset: PresetPlantilla,
        options: {
            darkModeSelector: '.tema-oscuro',
            cssLayer: {name: 'primeng', order: 'primeng'}
        }
    }
};
